import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Settings, Save, Shield, Layout } from 'lucide-react';

interface PlatformSettingsProps {
  settings: {
    theme: 'light' | 'dark' | 'system';
    compactView: boolean;
    autoRefresh: boolean;
    refreshInterval: number;
    desktopNotifications: boolean;
    soundAlerts: boolean;
    taskReminders: boolean;
    urgentAlertsOnly: boolean;
  };
  onSettingsChange: (settings: any) => void;
  onSave: () => void;
}

export const PlatformSettings: React.FC<PlatformSettingsProps> = ({
  settings,
  onSettingsChange,
  onSave,
}) => {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Settings className="w-5 h-5 text-purple-600" />
          </div>
          <h2 className="text-lg font-semibold">Platform Settings</h2>
        </div>
        <Button
          variant="primary"
          size="sm"
          icon={<Save className="w-4 h-4" />}
          onClick={onSave}
        >
          Save Settings
        </Button>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <Layout className="w-4 h-4" /> Appearance
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-2">Theme</label>
              <select
                value={settings.theme}
                onChange={(e) => onSettingsChange({ ...settings, theme: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.compactView}
                onChange={(e) => onSettingsChange({ ...settings, compactView: e.target.checked })}
                className="rounded text-purple-500"
              />
              <span className="text-sm text-gray-600">Use compact view</span>
            </label>
          </div>
        </div>

        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <Shield className="w-4 h-4" /> Notifications & Alerts
          </h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.desktopNotifications}
                onChange={(e) => onSettingsChange({ ...settings, desktopNotifications: e.target.checked })}
                className="rounded text-purple-500"
              />
              <span className="text-sm text-gray-600">Enable desktop notifications</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.soundAlerts}
                onChange={(e) => onSettingsChange({ ...settings, soundAlerts: e.target.checked })}
                className="rounded text-purple-500"
              />
              <span className="text-sm text-gray-600">Enable sound alerts</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.taskReminders}
                onChange={(e) => onSettingsChange({ ...settings, taskReminders: e.target.checked })}
                className="rounded text-purple-500"
              />
              <span className="text-sm text-gray-600">Task reminders</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.urgentAlertsOnly}
                onChange={(e) => onSettingsChange({ ...settings, urgentAlertsOnly: e.target.checked })}
                className="rounded text-purple-500"
              />
              <span className="text-sm text-gray-600">Only notify for urgent alerts</span>
            </label>
          </div>
        </div>

        <div>
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
            <Settings className="w-4 h-4" /> Auto-Refresh
          </h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.autoRefresh}
                onChange={(e) => onSettingsChange({ ...settings, autoRefresh: e.target.checked })}
                className="rounded text-purple-500"
              />
              <span className="text-sm text-gray-600">Enable auto-refresh</span>
            </label>
            {settings.autoRefresh && (
              <div>
                <label className="block text-sm text-gray-600 mb-2">Refresh Interval</label>
                <select
                  value={settings.refreshInterval}
                  onChange={(e) => onSettingsChange({ ...settings, refreshInterval: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value={30}>30 seconds</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes</option>
                  <option value={600}>10 minutes</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};